import { useEffect, useRef, useState } from "react";
import {
  useGetCrmMasterDetail,
  type CrmMasterDetail,
  type CrmRecordDetail,
} from "@/lib/api/crm/masters";
import { useApplyCrmRecordChanges } from "@/lib/api/crm/records";
import { useGetCrmRules, type CrmRules } from "@/lib/api/crm/rules";
import type { ApplyCrmRecordChangesResult } from "@/lib/api/crm/types";
import { ApplyCrmRecordChangesRequestValidation } from "@/lib/validation/crm";
import {
  issuesToFormSubmitError,
  toFormSubmitError,
  type FormSubmitError,
} from "./recordFormErrors";
import type { RecordFormValues } from "./recordFormSchema";
import { hasRecordChanges, toApplyChangesRequest, toRecordFormValues } from "./recordFormValues";

/** The record to edit: by its id, or by the customer it's linked to. */
export type RecordEditorTarget = { record_id: string } | { customer_id: string };

function isTarget(record: CrmRecordDetail, target: RecordEditorTarget): boolean {
  return "record_id" in target ? record.id === target.record_id : record.customer_id === target.customer_id;
}

/** What the form was opened with. Background refetches don't change it; only "Load their changes" does. */
type OpenedRecord = {
  master: CrmMasterDetail;
  record: CrmRecordDetail;
  /** The stored record as form values: what changes are worked out against. */
  values: RecordFormValues;
  /** What the form starts with: `values`, plus anything the caller adds (see `prepareValues`). */
  formValues: RecordFormValues;
  /** Bumped on every (re)load so the form remounts with the new values. */
  version: number;
};

function openRecord(
  rules: CrmRules,
  master: CrmMasterDetail,
  target: RecordEditorTarget,
  version: number,
  prepareValues: RecordEditorOptions["prepareValues"],
): OpenedRecord | null {
  const record = master.records.find((candidate) => isTarget(candidate, target));
  if (!record) return null;
  const values = toRecordFormValues(rules, record);
  return { master, record, values, formValues: prepareValues ? prepareValues(rules, values) : values, version };
}

export type RecordEditorOptions = {
  target: RecordEditorTarget;
  onSaved: (result: ApplyCrmRecordChangesResult) => void;
  /** Adds to what the form starts with (e.g. an empty contact row to fill in). Not part of the stored values. */
  prepareValues?: (rules: CrmRules, values: RecordFormValues) => RecordFormValues;
  /** A caller's own check before saving; an error stops the save. */
  validate?: (rules: CrmRules, values: RecordFormValues) => FormSubmitError | null;
};

/** The RecordForm props of an opened record ("update" mode). */
export type RecordEditorForm = {
  rules: CrmRules;
  defaultValues: RecordFormValues;
  groupRecords: CrmRecordDetail[];
  groupName: string;
  hasChanges: (values: RecordFormValues) => boolean;
  onSubmit: (values: RecordFormValues) => Promise<FormSubmitError | null>;
  onReload: () => Promise<void>;
};

export type RecordEditorState =
  | { status: "loading" }
  | { status: "error"; isRetrying: boolean; retry: () => void }
  | {
    status: "open";
    master: CrmMasterDetail;
    record: CrmRecordDetail;
    /** Remount the form with this key so a reload resets it. */
    version: number;
    form: RecordEditorForm;
  };

/** Loads a record's group and opens the record for editing; saves with apply-changes. */
export function useRecordEditor({ target, onSaved, prepareValues, validate }: RecordEditorOptions): RecordEditorState {
  const targetId = "record_id" in target ? target.record_id : target.customer_id;
  // Callers pass a new target object and prepareValues function on every render; the effect below reads
  // the latest ones from here and reruns only when the target itself (kind and id) changes.
  const targetKey = `${"record_id" in target ? "record" : "customer"}:${targetId}`;
  const latest = useRef({ target, prepareValues });
  latest.current = { target, prepareValues };
  const rulesResult = useGetCrmRules();
  const detailResult = useGetCrmMasterDetail(target, targetId !== "");
  const applyMutation = useApplyCrmRecordChanges();
  const [opened, setOpened] = useState<OpenedRecord | null>(null);

  const rules = rulesResult.data;
  const master = detailResult.data;
  const isOpen = opened !== null && isTarget(opened.record, target);

  useEffect((): void => {
    if (!isOpen && rules && master) {
      setOpened(openRecord(rules, master, latest.current.target, 0, latest.current.prepareValues));
    }
  }, [isOpen, rules, master, targetKey]);

  if (!isOpen || !rules) {
    const recordLoaded = master?.records.some((candidate) => isTarget(candidate, target)) ?? false;
    const isLoading = rulesResult.isLoading
      || detailResult.isLoading
      // Loaded but not opened yet (the effect runs next), or still fetching another record's group.
      || (recordLoaded && Boolean(rules))
      || (!recordLoaded && detailResult.isFetching);
    if (isLoading) return { status: "loading" };
    return {
      status: "error",
      isRetrying: detailResult.isFetching || rulesResult.isFetching,
      retry: () => {
        if (!rules) void rulesResult.refetch();
        void detailResult.refetch();
      },
    };
  }

  const { record, values: originalValues, version } = opened;

  const onSubmit = async (values: RecordFormValues): Promise<FormSubmitError | null> => {
    const invalid = validate?.(rules, values);
    if (invalid) return invalid;
    const built = toApplyChangesRequest(rules, record, originalValues, values);
    const parsed = ApplyCrmRecordChangesRequestValidation.safeParse(built.request);
    if (!parsed.success) return issuesToFormSubmitError(parsed.error.issues, built.rowIndexMap);

    const result = await applyMutation.mutateAsync(parsed.data);
    if (!result.success) return toFormSubmitError(rules, result, values, built.rowIndexMap);
    onSaved(result.data);
    return null;
  };

  const onReload = async (): Promise<void> => {
    const refetched = await detailResult.refetch();
    if (refetched.data) {
      // A record that's gone leaves `opened` null, which shows the "couldn't open" state.
      setOpened(openRecord(rules, refetched.data, target, version + 1, prepareValues));
    }
  };

  return {
    status: "open",
    master: opened.master,
    record,
    version,
    form: {
      rules,
      defaultValues: opened.formValues,
      groupRecords: opened.master.records,
      groupName: opened.master.long_name,
      hasChanges: (values: RecordFormValues) => hasRecordChanges(rules, record, originalValues, values),
      onSubmit,
      onReload,
    },
  };
}
